import React from "react";
import { useLocation, useNavigate } from "react-router";
import {
  LiveboardEmbed as ThoughtSpotLiveboardEmbed,
  Action,
} from "@thoughtspot/visual-embed-sdk/react";
import { COMMON_HIDDEN_ACTIONS } from "@/thoughtspot/constants";
import { useCaptureLiveboardView } from "@/hooks/useCaptureLiveboardView";
import { useThoughtSpotEmbedCommonProps } from "@/thoughtspot";
import { useUserPrivileges } from "@/hooks/useUserPrivileges";
import { useTheme } from "@/hooks/useTheme";

const baseHiddenActions: Action[] = [
  ...COMMON_HIDDEN_ACTIONS,
  Action.CopyAndEdit,
  Action.EditTitle,
  Action.RenameModalTitleDescription,
  Action.Pin,
  Action.SpotIQAnalyze,
  Action.Explore,
  Action.AddToFavorites,
];

export function LiveboardEmbed() {
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const privileges = useUserPrivileges();

  const hiddenActions = React.useMemo(() => {
    const actions = [...baseHiddenActions];

    if (!privileges.drill_down) {
      actions.push(Action.DrillDown);
    }

    return actions;
  }, [privileges.drill_down]);

  const insightsMatch = location.pathname.match(/\/insights\/pinboard\/([^/]+)/);
  const regularMatch = location.pathname.match(/\/pinboard\/([^/]+)/);
  const liveboardId = insightsMatch ? insightsMatch[1] : regularMatch ? regularMatch[1] : null;

  React.useLayoutEffect(() => {
    if (insightsMatch && liveboardId) {
      navigate(`/pinboard/${liveboardId}`, { replace: true });
    }
  }, [insightsMatch, liveboardId, navigate]);

  /****** LIVEBOARD VIEW CAPTURING ******/

  const lastLiveboardId = React.useRef<string | null>(null);
  const captureLiveboardView = useCaptureLiveboardView();
  React.useEffect(() => {
    const liveboardIdDidNotChange = liveboardId === lastLiveboardId.current;

    lastLiveboardId.current = liveboardId;

    if (!liveboardId || liveboardIdDidNotChange) {
      return;
    }
    captureLiveboardView(liveboardId);
  }, [liveboardId, captureLiveboardView]);

  /**************************************/

  const commonProps = useThoughtSpotEmbedCommonProps();

  if (!liveboardId) {
    return null;
  }

  return (
    <>
      <ThoughtSpotLiveboardEmbed
        {...commonProps}
        key={`liveboard-embed-${theme}`}
        className="w-full h-full"
        liveboardId={liveboardId}
        showLiveboardTitle={true}
        showLiveboardDescription={true}
        hiddenActions={hiddenActions}
        enable2ColumnLayout
        isLiveboardStylingAndGroupingEnabled
      />
    </>
  );
}
